import type { Concept } from '@yuan-math/curriculum';

/**
 * Algebra I — the first high-school band.
 *
 * Grade 8 made a relationship into an object. Algebra I makes an *expression*
 * into one: something with a shape you can change without changing its value,
 * and whose shape is what lets you answer questions about it.
 *
 * That is the thread running through all of it. Factorising does not simplify
 * x² + 5x + 6 in any numerical sense — the two forms agree at every value of x.
 * What (x + 2)(x + 3) has that the sum does not is a *structure* that makes the
 * roots readable, and the whole of quadratics follows from noticing that.
 *
 * Two orderings deserve their comments. `quadratics` depends on `factoring`
 * rather than on `systems-of-equations`, because solving by factoring is where
 * the topic begins; and `quadratic-formula` comes after both `quadratics` and
 * `radicals`, since a formula whose output is √17 is useless without the means
 * to write it.
 *
 * Three concepts sit *before* the polynomials, and they are here rather than in
 * Grade 8 because each is the same move made on a familiar object. Rearranging
 * a formula is solving an equation whose other letters happen to be numbers you
 * were not told; a domain is the question "which inputs are allowed" asked of a
 * machine the student has been feeding for a year without asking; and an
 * inequality in two variables is the number-line ray with a second axis added,
 * so the answer becomes a region rather than a stretch.
 *
 * `completing-the-square` is the one addition that changes an existing edge.
 * `quadratic-graphs` already reads the vertex off completed-square form and
 * `quadratic-formula` is that form applied once to ax² + bx + c, so without it
 * the course hands a student two results and no derivation. It is therefore a
 * prerequisite of the formula, not an optional aside.
 */
export const ALGEBRA_ONE_CONCEPTS: Concept[] = [
  /* -------------------------------------------------------- the numbers */
  {
    id: 'real-numbers',
    title: 'The Real Numbers',
    summary:
      'The nested families of number met so far, and the handful of rules that licence every rearrangement in the course ahead.',
    strand: 'numbers',
    levels: ['grade-9'],
    prerequisites: ['rational-numbers', 'square-roots'],
    status: 'available',
    estimatedMinutes: 30,
  },

  /* ---------------------------------------------------- linear machinery */
  {
    id: 'equations-with-fractions',
    title: 'Equations with Fractions',
    summary: 'Clearing every denominator in one move, so an equation with fractions becomes one without.',
    strand: 'algebra',
    levels: ['grade-9'],
    prerequisites: ['multi-step-equations', 'common-denominators'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'compound-inequalities',
    title: 'Compound Inequalities',
    summary: 'Two conditions at once — and the whole difference between wanting both of them and wanting either.',
    strand: 'algebra',
    levels: ['grade-9'],
    prerequisites: ['inequalities'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'linear-equation-forms',
    title: 'Forms of a Linear Equation',
    summary: 'The same line written three ways, and the fact each way was designed to make obvious.',
    strand: 'functions',
    levels: ['grade-9'],
    prerequisites: ['linear-functions'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* --------------------------------------------------- before the algebra */
  {
    id: 'literal-equations',
    title: 'Rearranging Formulas',
    summary: 'Solving for the letter you want, when every other letter is a number you were not given.',
    strand: 'algebra',
    levels: ['grade-9'],
    prerequisites: ['multi-step-equations', 'algebraic-expressions'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'domain-and-range',
    title: 'Domain and Range',
    summary: 'Which inputs a function accepts, and which outputs it can actually produce.',
    strand: 'functions',
    levels: ['grade-9'],
    prerequisites: ['functions-intro', 'inequalities'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'two-variable-inequalities',
    title: 'Linear Inequalities in Two Variables',
    summary: 'A boundary line and the half of the plane on one side of it — and how to tell which half.',
    strand: 'algebra',
    levels: ['grade-9'],
    prerequisites: ['inequalities', 'linear-functions', 'systems-of-equations'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* --------------------------------------------------------- expressions */
  {
    id: 'polynomials',
    title: 'Polynomials',
    summary: 'Adding, subtracting and multiplying expressions with powers — and why every term meets every term.',
    strand: 'algebra',
    levels: ['grade-9', 'grade-10'],
    prerequisites: ['exponent-rules', 'algebraic-expressions'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'factoring',
    title: 'Factoring',
    summary: 'Running multiplication backwards, to recover the structure an expanded expression hides.',
    strand: 'algebra',
    levels: ['grade-9', 'grade-10'],
    prerequisites: ['polynomials', 'factors-multiples'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'radicals',
    title: 'Simplifying Radicals',
    summary: 'Writing an irrational number in its plainest exact form, rather than rounding it away.',
    strand: 'numbers',
    levels: ['grade-9', 'grade-10'],
    prerequisites: ['square-roots', 'exponent-rules'],
    status: 'available',
    estimatedMinutes: 30,
  },

  /* ---------------------------------------------------------- quadratics */
  {
    id: 'quadratics',
    title: 'Solving Quadratic Equations',
    summary: 'Two solutions rather than one, and the zero-product rule that finds them.',
    strand: 'algebra',
    levels: ['grade-9', 'grade-10'],
    prerequisites: ['factoring', 'square-roots'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'quadratic-graphs',
    title: 'Quadratic Functions and Parabolas',
    summary: 'The curve behind the equation — where its lowest point is, and where it crosses.',
    strand: 'functions',
    levels: ['grade-9'],
    prerequisites: ['quadratics', 'functions-intro'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'completing-the-square',
    title: 'Completing the Square',
    summary: 'Rebuilding a quadratic around a perfect square, which is where both the vertex and the formula come from.',
    strand: 'algebra',
    levels: ['grade-9'],
    prerequisites: ['quadratics', 'radicals'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'quadratic-formula',
    title: 'The Quadratic Formula',
    summary: 'The solution to every quadratic there is — and a way to know how many there are before solving.',
    strand: 'algebra',
    levels: ['grade-9', 'grade-10'],
    prerequisites: ['quadratics', 'radicals', 'completing-the-square'],
    status: 'available',
    estimatedMinutes: 40,
  },

  /* ----------------------------------------------------------- functions */
  {
    id: 'exponential-functions',
    title: 'Exponential Growth and Decay',
    summary: 'Change that multiplies rather than adds — and why it always overtakes a straight line eventually.',
    strand: 'functions',
    levels: ['grade-9', 'grade-10'],
    prerequisites: ['functions-intro', 'exponent-rules', 'percent-applications'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'sequences',
    title: 'Arithmetic and Geometric Sequences',
    summary: 'Patterns of numbers, and the two ways a pattern can carry on: by adding, or by multiplying.',
    strand: 'discrete',
    levels: ['grade-9'],
    prerequisites: ['linear-functions', 'exponential-functions'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* -------------------------------------------------- the awkward corners */
  {
    id: 'rational-expressions',
    title: 'Rational Expressions',
    summary: 'Fractions whose top and bottom are polynomials — where only factors may cancel, never terms.',
    strand: 'algebra',
    levels: ['grade-9', 'grade-10'],
    prerequisites: ['factoring', 'divide-fractions'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'absolute-value',
    title: 'Absolute Value Equations and Inequalities',
    summary: 'Distance from zero — which is why these have two answers, or none, and why one inequality splits and the other does not.',
    strand: 'algebra',
    levels: ['grade-9'],
    /*
     * `compound-inequalities` rather than plain `inequalities`: |x| < 5 *is*
     * −5 < x < 5 and |x| > 5 *is* an "or", so a student who has not met the
     * two forms separately is being asked to learn both at once, in the one
     * topic where telling them apart is the entire difficulty.
     */
    prerequisites: ['compound-inequalities', 'absolute-value-opposites'],
    status: 'available',
    estimatedMinutes: 30,
  },

  /* ---------------------------------------------------------- statistics */
  {
    id: 'standard-deviation',
    title: 'Standard Deviation',
    summary: 'The measure of spread the rest of statistics is built on — and why it squares the distances before averaging them.',
    strand: 'statistics',
    levels: ['grade-9'],
    prerequisites: ['data-variability', 'square-roots'],
    status: 'available',
    estimatedMinutes: 35,
  },
];
